import { FC } from 'react'
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion'
import { STEP_IDS } from './onboardingSteps'
import { StepProps } from './types'
import TeamStep from './steps/TeamStep'
import RosterStep from './steps/RosterStep'
import EventsStep from './steps/EventsStep'
import RecruitmentStep from './steps/RecruitmentStep'
import LootStep from './steps/LootStep'
import NotificationsStep from './steps/NotificationsStep'
import AccessStep from './steps/AccessStep'
import OnboardingTeamProvider from './OnboardingTeamProvider'

const STEP_COMPONENTS: Record<typeof STEP_IDS[number], FC<StepProps>> = {
  team: TeamStep,
  roster: RosterStep,
  events: EventsStep,
  recruitment: RecruitmentStep,
  loot: LootStep,
  notifications: NotificationsStep,
  access: AccessStep,
}

interface Props extends Omit<StepProps, 'isFirst' | 'isLast'> {
  stepIndex: number
  guildReference?: wowaudit.GuildReference
}

const OnboardingWizard = ({
  guild,
  guildReference,
  stepIndex,
  createdTeam,
  onTeamCreated,
  onFinish,
  goNext,
  goBack,
}: Props) => {
  const reduceMotion = useReducedMotion()

  const stepProps: StepProps = {
    guild,
    createdTeam,
    onTeamCreated,
    onFinish,
    goNext,
    goBack,
    isFirst: stepIndex === 0,
    isLast: stepIndex === STEP_IDS.length - 1,
  }

  const renderStep = () => {
    const StepComponent = STEP_COMPONENTS[STEP_IDS[stepIndex]]
    if (!StepComponent) return null

    if (stepIndex === 0) return <StepComponent {...stepProps} />

    if (!createdTeam) return null

    return (
      <OnboardingTeamProvider
        guild={guild}
        guildReference={guildReference}
        createdTeam={createdTeam}
      >
        <StepComponent {...stepProps} />
      </OnboardingTeamProvider>
    )
  }

  return (
    <AnimatePresence mode='wait'>
      <motion.div
        key={stepIndex}
        initial={reduceMotion ? { opacity: 0 } : { opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        exit={reduceMotion ? { opacity: 0 } : { opacity: 0, y: -12 }}
        transition={{ duration: 0.25, ease: 'easeOut' }}
      >
        {renderStep()}
      </motion.div>
    </AnimatePresence>
  )
}

export default OnboardingWizard
